/**
 * Keep long-lived tabs on the latest deploy.
 *
 * An open tab keeps running the exact JS bundle it first loaded. After a deploy
 * the hashed chunk filenames change, so a stale tab can break — dead buttons, a
 * lazy route that 404s, a hung spinner — until the user manually hard-refreshes.
 * This makes a tab notice when a newer build is live and reload onto it on its
 * own, at a moment that won't interrupt the user.
 *
 * Detecting a new build WITHOUT any build-time manifest: remember the hashed
 * asset URLs referenced by the HTML this tab booted with, then re-fetch the live
 * index.html (network-first, so never the cached copy) and compare. A different
 * asset set means a new build shipped.
 *
 * When it reloads: at the natural "coming back to the tab" moments (focus /
 * visibility) and, for a tab left open a long time, whenever the tab is
 * backgrounded — but never while the user is actively typing, so no in-progress
 * input is lost. Pairs with the `vite:preloadError` handler in main.tsx, which
 * is the reactive safety net for a chunk that fails before this proactive check
 * catches it.
 */

/** The hashed /assets/*.js URLs referenced by an HTML string (deduped, sorted). */
function assetSignature(html: string): string {
  const refs = html.match(/\/assets\/[A-Za-z0-9_.-]+\.js/g) ?? [];
  return [...new Set(refs)].sort().join('|');
}

// Captured at import time — before React mounts or any lazy chunk is appended —
// so it reflects the originally-served index.html, not the mutated live DOM.
const BOOT_SIGNATURE = assetSignature(document.documentElement.outerHTML);

let pendingUpdate = false;
let reloading = false;

/** Whether a build newer than the one this tab booted with is now live. */
async function newBuildIsLive(): Promise<boolean> {
  try {
    // Accept: text/html routes this through the service worker's network-first
    // HTML path, so we always compare against the freshly-deployed shell rather
    // than a cached copy. no-store keeps any HTTP cache out of it too.
    const res = await fetch('/', {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
    });
    if (!res.ok) return false;
    const sig = assetSignature(await res.text());
    if (!sig) return false; // couldn't parse an asset set — never act on nothing
    return sig !== BOOT_SIGNATURE;
  } catch {
    return false; // offline / transient — never disrupt the user
  }
}

function reloadNow(): void {
  if (reloading) return;
  reloading = true;
  try {
    // Start the fresh load with a clean preload-reload guard.
    sessionStorage.removeItem('entredash.preloadReloadedAt');
  } catch {
    /* sessionStorage unavailable — fine */
  }
  window.location.reload();
}

// Last touch/click/key, so a reload never lands in the middle of a tap sequence
// (e.g. the member opened the menu sheet and is choosing a section).
let lastInteraction = 0;
const markInteraction = (): void => {
  lastInteraction = Date.now();
};
/** The member touched the app in the last few seconds: reloading now would eat their tap. */
function recentlyActive(): boolean {
  return Date.now() - lastInteraction < 8000;
}

/**
 * With an update pending, the next in-app link is followed as a full page load
 * instead of a client-side route change. The member was leaving the screen
 * anyway, so they land on the new build with nothing lost and no dead tap.
 */
function onLinkClick(e: MouseEvent): void {
  if (!pendingUpdate || reloading || e.defaultPrevented || e.button !== 0) return;
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
  if (!a || a.target === '_blank' || a.hasAttribute('download')) return;
  const url = new URL(a.href, window.location.href);
  if (url.origin !== window.location.origin) return;
  // preventDefault only: the router sees it and skips its own client-side
  // navigation, but the link's own handlers still run, so the phone menu sheet
  // closes and page scroll unlocks. (Stopping propagation here swallowed the
  // tap: the sheet stayed open over the page and the app looked frozen while
  // the new build loaded.)
  e.preventDefault();
  reloading = true;
  showLoadingBar();
  window.location.assign(url.href);
}

/** A thin top bar so the member sees the tap landed while the fresh build loads. */
function showLoadingBar(): void {
  try {
    const bar = document.createElement('div');
    bar.setAttribute('aria-hidden', 'true');
    bar.style.cssText =
      'position:fixed;top:0;left:0;height:3px;width:0;z-index:99999;background:#4fd992;transition:width 1.6s cubic-bezier(.2,.7,.3,1);pointer-events:none';
    document.body.appendChild(bar);
    requestAnimationFrame(() => {
      bar.style.width = '85%';
    });
  } catch {
    /* purely cosmetic */
  }
}

/** True when the user is mid-typing, so a reload would lose in-progress input. */
function isTyping(): boolean {
  const el = document.activeElement as HTMLElement | null;
  const tag = el?.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || !!el?.isContentEditable;
}

async function check(): Promise<void> {
  if (pendingUpdate || reloading) return;
  if (await newBuildIsLive()) pendingUpdate = true;
}

export function startAutoUpdate(): void {
  // Returning to the tab is the natural moment to refresh onto a new build:
  // check for one, and if there is one, reload unless the user is typing.
  // Never reload under the member's finger: installed home-screen apps resume
  // exactly when the member starts tapping, and a reload landing a second later
  // swallowed their taps (the phone menu looked frozen). If they've touched the
  // app since coming back, the update waits for their next link or for the app
  // to go to the background.
  const onReturn = async (): Promise<void> => {
    await check();
    if (pendingUpdate && !isTyping() && !recentlyActive()) reloadNow();
  };
  // Leaving the tab (hidden/blurred) is a zero-disruption moment: if an update
  // is already pending, reload silently so the tab is fresh when they come back.
  const onLeave = (): void => {
    if (pendingUpdate) reloadNow();
  };

  for (const type of ['pointerdown', 'keydown', 'touchstart'] as const) {
    window.addEventListener(type, markInteraction, { capture: true, passive: true });
  }
  // Capture phase, so it runs before the router's own click handler.
  document.addEventListener('click', onLinkClick, true);

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void onReturn();
    else onLeave();
  });
  window.addEventListener('focus', () => void onReturn());
  // Only a truly hidden app counts as "leaving". On phones `blur` also fires
  // for system overlays (notification shade, keyboard, share sheets) while the
  // member is still using the app, which reloaded it mid-use.
  window.addEventListener('pagehide', onLeave);

  // A tab left focused for a long time still checks periodically; the reload
  // itself waits for the next leave/return so it can't interrupt active work.
  setInterval(() => void check(), 3 * 60 * 1000);
  // And once shortly after boot, to catch a tab opened moments before a deploy.
  setTimeout(() => void onReturn(), 20 * 1000);
}
